import type { Gift, IncludedItem, Review, FaqItem, RichLine } from '../types/content'

/** Destination for every "Aggiungi al carrello" action on the page. */
export const checkoutUrl = 'https://myeasyguide.online/cart/49902595670258:1'

/** Same cart, plus the NeuroKids order bump when the offer is accepted. */
export const checkoutUrlWithBump = `${checkoutUrl},49945662128370:1`

/** Numeric price of the kit, used to report the order value to Meta. */
export const kitPriceValue = 27.99

export const images = {
  heroBox: 'https://cdn.magicpatterns.com/uploads/3kDjWTk4Z6pMagau7TxTqp/megakit_piccolo_ingegnere_italiano_transparente.webp',
  bottleRobot: 'https://cdn.magicpatterns.com/patterns/generated-images/839b3536-4406-4dca-9489-5ca19a85de51.jpg',
  spiralCandle: 'https://cdn.magicpatterns.com/patterns/generated-images/d5b2134d-7284-454e-a975-e2370889ec7a.jpg',
  cupRobot: 'https://cdn.magicpatterns.com/patterns/generated-images/c7afa25d-1b55-461f-acc0-c0b74489688f.jpg',
  balloonCar: 'https://cdn.magicpatterns.com/patterns/generated-images/1b77d90c-0891-4c79-a619-a4c81761be7a.jpg',
  dadDaughterRobot: 'https://cdn.magicpatterns.com/patterns/generated-images/8e60ade3-a3bd-473a-bd87-1981ee785bca.jpg',
  dadDaughterCrafting: 'https://cdn.magicpatterns.com/patterns/generated-images/26993425-d121-4a8b-b522-6d7314851a4b.jpg',
  boyCrafting: 'https://cdn.magicpatterns.com/patterns/generated-images/264272c6-4177-4bef-99b9-5697d7513e4a.jpg',
  bottleRocket: 'https://cdn.magicpatterns.com/patterns/generated-images/29df76db-cefd-466b-a31e-33832ca13543.jpg',
}

export const painPoints: RichLine[] = [
  [
    { text: 'Tuo figlio passa ore ' },
    { text: 'incollato allo schermo', tone: 'brick' },
    { text: ' e ' },
    { text: 'ormai non sai più cosa fare', tone: 'navy' },
    { text: '.' },
  ],
  [
    { text: 'Ogni volta che gli togli il telefono, ' },
    { text: 'parte la scenata', tone: 'brick' },
    { text: '.' },
  ],
  [
    { text: 'Ti spaventa pensare a ' },
    { text: 'come sarà tra qualche anno', tone: 'navy' },
    { text: ', se cresce ' },
    { text: 'sempre davanti a uno schermo', tone: 'brick' },
    { text: '.' },
  ],
  [
    { text: 'Gli dai il telefono e subito dopo ' },
    { text: 'te ne penti', tone: 'brick' },
    { text: '.' },
  ],
  [
    { text: 'Ami tuo figlio da morire, ma ' },
    { text: 'non ti senti il genitore che vorresti essere', tone: 'navy' },
    { text: '.' },
  ],
]

export const includedItems: IncludedItem[] = [
  {
    title: 'Robot di cartone',
    detail: 'con bracci, parti mobili e meccanismi semplici da costruire con quello che trovi in casa.',
  },
  {
    title: 'Esperimenti scientifici',
    detail: 'spiegati passo passo, con ingredienti e oggetti che hai già in cucina.',
  },
  {
    title: 'Costruzioni e veicoli',
    detail: 'macchinine, ponti, gru e meccanismi che si muovono davvero.',
  },
  {
    title: 'Lavoretti creativi',
    detail: 'con cartone, carta, bottiglie, plastilina, palloncini e altri materiali di recupero.',
  },
  {
    title: 'Attività di concentrazione, matematica e manualità',
    detail: 'per imparare, superare sfide e allenarsi giocando.',
  },
  {
    title: 'Accesso digitale immediato e per sempre',
    detail: 'senza abbonamenti e senza scadenza.',
  },
]

export const outcomes: RichLine[] = [
  [
    { text: 'Trasformi i pomeriggi caotici in ' },
    { text: 'momenti di gioco e risate', tone: 'forest' },
    { text: ', senza dover alzare la voce.' },
  ],
  [
    { text: 'Tuo figlio inizia a ' },
    { text: 'costruire con le sue mani', tone: 'navy' },
    { text: ' invece di buttarsi subito sul telefono.' },
  ],
  [
    { text: 'Stabilisci ' },
    { text: 'regole chiare sull’uso dello schermo', tone: 'forest' },
    { text: ' senza urlare e senza punizioni.' },
  ],
  [
    { text: 'Tuo figlio smette di chiedere il telefono ogni volta che si annoia e inizia a ' },
    { text: 'creare per curiosità e per gioco', tone: 'navy' },
    { text: '.' },
  ],
  [
    { text: 'Scopri che si può crescere un figlio con ' },
    { text: 'meno schermi e senza sentirsi in colpa', tone: 'forest' },
    { text: '.' },
  ],
]

/**
 * Bonus content. Each gift is an independent entry — edit the label, title or
 * image of any single bonus here without touching the rest of the section.
 *
 * Italian typography note: only the first word is capitalized. English-style
 * title case is the single loudest signal of a translated page.
 */
export const gifts: Gift[] = [
  {
    label: 'Bonus 1',
    title: 'Piano d’azione da 150 attività',
    subtitle: 'Planner settimanali, tabelle dei progressi e checklist pronte da spuntare',
    image: 'https://cdn.magicpatterns.com/uploads/5PMhYgQMMvvepkkFq7rLEo/image.png',
  },
  {
    label: 'Bonus 2',
    title: 'Guida sensoriale per i più piccoli',
    subtitle: 'Oltre 50 attività pratiche per la concentrazione, la calma e la coordinazione',
    image: 'https://cdn.magicpatterns.com/uploads/6XsqZjn5jGpnd6P94H4Yy1/image.png',
  },
  {
    label: 'Bonus 3',
    title: 'Kit per l’angolo dei compiti',
    subtitle: 'Oltre 100 pagine da stampare per rendere la scrivania un posto dove viene voglia di stare',
    image: 'https://cdn.magicpatterns.com/uploads/9Ra7oxewksGXfh3pSRVp9H/image.png',
  },
  {
    label: 'Bonus 4',
    title: 'Pacchetto completo di schede didattiche',
    subtitle: 'Un anno intero di schede e lezioni dalla materna alla quinta elementare',
    image: 'https://cdn.magicpatterns.com/uploads/2qtW1dANp1Z4hZZgrTozRZ/image.png',
  },
]

export const reviews: Review[] = [
  {
    name: 'Giulia M.',
    country: 'Milano',
    rating: 5,
    text: 'Pensavo fosse complicato mettermi lì a costruire qualcosa, invece le guide sono chiarissime. Mio figlio ha fatto il suo primo robot con roba che avevamo già in cucina.',
  },
  {
    name: 'Francesca R.',
    country: 'Roma',
    rating: 5,
    text: 'La cosa migliore è che non ho dovuto comprare niente di speciale. Cartone, tappi di bottiglia e via. Quaranta minuti di silenzio assoluto mentre lavoro.',
  },
  {
    name: 'Chiara B.',
    country: 'Torino',
    rating: 4,
    text: 'Molto bello, anche se alcuni progetti mi hanno preso più tempo di quanto dicesse la guida. Ne vale comunque la pena, mia figlia non ha mollato il lavoretto per tutto il pomeriggio.',
  },
  {
    name: 'Marco T.',
    country: 'Bergamo',
    rating: 5,
    text: 'Credevo di dovermi mettere lì a spiegare tutto io. Manco per idea: i passaggi sono chiari e se li fanno da soli.',
  },
  {
    name: 'Elisa D.',
    country: 'Napoli',
    rating: 5,
    text: 'Stampiamo un progetto a settimana ed è diventato il nostro appuntamento della domenica. I miei due bambini litigano per chi taglia il cartone.',
  },
  {
    name: 'Martina P.',
    country: 'Padova',
    rating: 5,
    text: 'L’ho preso tanto per provare e alla fine è diventato la cosa che usiamo di più in casa questo mese. Si vede che l’ha pensato qualcuno che i bambini li conosce davvero.',
  },
]

export const faqs: FaqItem[] = [
  {
    question: 'È un prodotto fisico o digitale?',
    answer:
      'È 100% digitale. Subito dopo l’acquisto ricevi un’email con l’accesso immediato a tutti i progetti e alle attività da stampare, quindi niente spedizione e niente attesa.',
  },
  {
    question: 'Per quale età è pensato?',
    answer:
      'Funziona al meglio tra i 4 e i 12 anni. Ogni attività indica l’età e il livello di difficoltà, così puoi scegliere qualcosa di più semplice o più impegnativo a seconda della giornata.',
  },
  {
    question: 'Servono materiali particolari?',
    answer:
      'No. Quasi tutto si costruisce con cartone, bottiglie, tappi, cannucce, nastro adesivo e altre cose che hai già in casa. Ogni progetto elenca i materiali prima di iniziare.',
  },
  {
    question: 'Serve una stampante?',
    answer:
      'Non sempre. Prima di ogni progetto trovi scritto se serve la stampante o se basta seguirlo dallo schermo del telefono, del tablet o del computer.',
  },
  {
    question: 'E se non ho tempo?',
    answer:
      'La maggior parte dei progetti richiede dai 10 ai 30 minuti. Puoi partire con una sola attività da 10 minuti al giorno e vedere comunque la differenza nei tuoi pomeriggi.',
  },
  {
    question: 'E se non fa per me?',
    answer:
      'Hai 7 giorni di garanzia soddisfatti o rimborsati. Prova i progetti e, se non è quello che fa per la tua famiglia, scrivici: ti rimborsiamo, senza farti troppe domande.',
  },
  {
    question: 'L’accesso ha una scadenza?',
    answer:
      'Mai. L’accesso è per sempre e include tutti gli aggiornamenti futuri del kit, senza costi aggiuntivi.',
  },
  {
    question: 'È sicuro pagare qui?',
    answer:
      'Sì. I pagamenti avvengono tramite una connessione criptata e non conserviamo mai i dati della tua carta.',
  },
]
